// wageTypesConfig.js
// Comprehensive wage types configuration for frontend
// This ensures consistency with backend wage types handling

import {
  Calculator,
  Clock,
  DollarSign,
  AlertTriangle,
  Car,
  Receipt,
  Building,
  Baby
} from 'lucide-react';

/**
 * Essential wage types configuration for frontend
 * Provides UI configuration for the 12 essential wage types
 *
 * Note: Non-essential types are commented out but can be re-enabled later
 * The actual list of available wage types comes from backend API (active flag in DB)
 */
export const COMPREHENSIVE_WAGE_TYPES = {
  // ============================================================================
  // BASIC SALARY (3 types) - ACTIVE
  // ============================================================================
  '1101': { // Månadslön
    unit: 'St',
    inputType: 'pieces',
    defaultQuantity: 1,
    label: 'Antal månader',
    requiresAbsenceDates: false,
    effect_type: 'inc',
    description: 'Månadslön',
    isPopular: true,
    quickActions: [1],
    icon: Calculator,
    color: 'var(--bs-primary)'
  },
  // '1103': { // Daglön - DISABLED (uncommon, can re-enable later)
  //   unit: 'Kal.dgr',
  //   inputType: 'days',
  //   defaultQuantity: 1,
  //   label: 'Dagar',
  //   requiresAbsenceDates: false,
  //   effect_type: 'inc',
  //   description: 'Dagpenning',
  //   icon: Calendar,
  //   color: '#10b981'
  // },
  '1106': { // Timlön
    unit: 'Tim',
    inputType: 'hours',
    defaultQuantity: 1,
    label: 'Timmar',
    requiresAbsenceDates: false,
    effect_type: 'inc',
    description: 'Övertid',
    icon: Clock,
    color: 'var(--bs-primary)'
  },
  '1172': { // Bonus
    unit: null,
    inputType: 'amount',
    defaultQuantity: 0,
    label: 'Belopp (SEK)',
    requiresAbsenceDates: false,
    effect_type: 'inc',
    description: 'Bonus eller tillägg',
    icon: DollarSign,
    color: '#f59e0b'
  },
  // '1181': { // Föräldrapenningstillägg - DISABLED (uncommon, can re-enable later)
  //   unit: null,
  //   inputType: 'amount',
  //   defaultQuantity: 0,
  //   label: 'Belopp (SEK)',
  //   requiresAbsenceDates: false,
  //   effect_type: 'inc',
  //   description: 'Föräldrapenningstillägg',
  //   icon: Gift,
  //   color: '#f59e0b'
  // },

  // ============================================================================
  // TRAVEL/MILEAGE (1 type) - ACTIVE
  // ============================================================================
  '1184': { // Milersättning skattepliktig
    unit: 'Km',
    inputType: 'distance',
    defaultQuantity: 1,
    label: 'Kilometer',
    requiresAbsenceDates: false,
    effect_type: 'inc',
    description: 'Milersättning skattepliktig',
    icon: Car,
    color: '#8b5cf6'
  },
  // '1200': { // Nattraktamente - DISABLED (add when needed for business travel)
  //   unit: 'St',
  //   inputType: 'pieces',
  //   defaultQuantity: 1,
  //   label: 'Antal',
  //   requiresAbsenceDates: false,
  //   effect_type: 'inc',
  //   description: 'Nattraktamente',
  //   icon: Utensils,
  //   color: '#f97316'
  // },
  // '1201': { // Traktamente helt - DISABLED (add when needed for business travel)
  //   unit: 'St',
  //   inputType: 'pieces',
  //   defaultQuantity: 1,
  //   label: 'Antal',
  //   requiresAbsenceDates: false,
  //   effect_type: 'inc',
  //   description: 'Traktamente helt',
  //   icon: Utensils,
  //   color: '#f97316'
  // },
  // '1202': { // Traktamente halvt - DISABLED (add when needed for business travel)
  //   unit: 'St',
  //   inputType: 'pieces',
  //   defaultQuantity: 1,
  //   label: 'Antal',
  //   requiresAbsenceDates: false,
  //   effect_type: 'inc',
  //   description: 'Traktamente halvt',
  //   icon: Utensils,
  //   color: '#f97316'
  // },
  // '1204': { // Utlandstraktamente ej skattepliktigt - DISABLED (add when needed)
  //   unit: 'St',
  //   inputType: 'pieces',
  //   defaultQuantity: 1,
  //   label: 'Antal',
  //   requiresAbsenceDates: false,
  //   effect_type: 'inc',
  //   description: 'Utlandstraktamente ej skattepliktigt',
  //   icon: MapPin,
  //   color: '#f97316'
  // },

  // ============================================================================
  // MEAL DEDUCTIONS - DISABLED (rarely used)
  // ============================================================================
  // '1209': { // Kostavdrag frukost - DISABLED (rarely used)
  //   unit: 'St',
  //   inputType: 'pieces',
  //   defaultQuantity: 1,
  //   label: 'Antal',
  //   requiresAbsenceDates: false,
  //   effect_type: 'dec',
  //   description: 'Kostavdrag frukost',
  //   icon: Utensils,
  //   color: '#ef4444'
  // },
  // '1210': { // Kostavdrag lunch eller middag - DISABLED (rarely used)
  //   unit: 'St',
  //   inputType: 'pieces',
  //   defaultQuantity: 1,
  //   label: 'Antal',
  //   requiresAbsenceDates: false,
  //   effect_type: 'dec',
  //   description: 'Kostavdrag lunch eller middag',
  //   icon: Utensils,
  //   color: '#ef4444'
  // },

  // ============================================================================
  // REPRESENTATION - DISABLED (add when needed)
  // ============================================================================
  // '1220': { // Representation, avdragsgill - DISABLED
  //   unit: null,
  //   inputType: 'amount',
  //   defaultQuantity: 0,
  //   label: 'Belopp (SEK)',
  //   requiresAbsenceDates: false,
  //   effect_type: 'dec',
  //   description: 'Representation, avdragsgill',
  //   icon: Receipt,
  //   color: '#ef4444'
  // },
  // '1221': { // Representation, ej avdragsgill - DISABLED
  //   unit: null,
  //   inputType: 'amount',
  //   defaultQuantity: 0,
  //   label: 'Belopp (SEK)',
  //   requiresAbsenceDates: false,
  //   effect_type: 'dec',
  //   description: 'Representation, ej avdragsgill',
  //   icon: Receipt,
  //   color: '#ef4444'
  // },

  // ============================================================================
  // EXPENSE REIMBURSEMENTS (2 types) - ACTIVE
  // ============================================================================
  '1222': { // Utlägg logi
    unit: null,
    inputType: 'amount',
    defaultQuantity: 0,
    label: 'Belopp (SEK)',
    requiresAbsenceDates: false,
    effect_type: 'dec',
    description: 'Utlägg logi',
    icon: Building,
    color: '#ef4444'
  },
  // '1223': { // Utlägg resekostnader - DISABLED (use 1224 for general expenses)
  //   unit: null,
  //   inputType: 'amount',
  //   defaultQuantity: 0,
  //   label: 'Belopp (SEK)',
  //   requiresAbsenceDates: false,
  //   effect_type: 'dec',
  //   description: 'Utlägg resekostnader',
  //   icon: MapPin,
  //   color: '#ef4444'
  // },
  '1224': { // Utlägg övrigt
    unit: null,
    inputType: 'amount',
    defaultQuantity: 0,
    label: 'Belopp (SEK)',
    requiresAbsenceDates: false,
    effect_type: 'dec',
    description: 'Utlägg övrigt',
    icon: Receipt,
    color: '#ef4444'
  },

  // ============================================================================
  // CAR BENEFITS - DISABLED (add when employee gets company car)
  // ============================================================================
  // '2101': { // Bilförmån - DISABLED
  //   unit: null,
  //   inputType: 'amount',
  //   defaultQuantity: 0,
  //   label: 'Belopp (SEK)',
  //   requiresAbsenceDates: false,
  //   effect_type: 'inc',
  //   description: 'Bilförmån',
  //   icon: Car,
  //   color: '#8b5cf6'
  // },
  // '2102': { // Bilförmån avdrag - DISABLED
  //   unit: null,
  //   inputType: 'amount',
  //   defaultQuantity: 0,
  //   label: 'Belopp (SEK)',
  //   requiresAbsenceDates: false,
  //   effect_type: 'dec',
  //   description: 'Bilförmån avdrag',
  //   icon: Car,
  //   color: '#ef4444'
  // },

  // ============================================================================
  // SICK LEAVE (1 type) - ACTIVE
  // ============================================================================
  '3101': { // Månadsavlönad karensdag
    unit: 'Tim',
    inputType: 'hours',
    defaultQuantity: 8,
    label: 'Timmar',
    requiresAbsenceDates: false,
    effect_type: 'dec',
    description: 'Månadsavlönad karensdag',
    icon: AlertTriangle,
    color: '#ef4444'
  },
  // '3103': { // Månadsavlönad karensavdrag - DISABLED (3101 covers this)
  //   unit: 'St',
  //   inputType: 'pieces',
  //   defaultQuantity: 1,
  //   label: 'Antal',
  //   requiresAbsenceDates: false,
  //   effect_type: 'dec',
  //   description: 'Månadsavlönad karensavdrag',
  //   icon: AlertTriangle,
  //   color: '#ef4444'
  // },
  // '3104': { // Karens Månadsavlönad - DISABLED (3101 covers this)
  //   unit: 'Tim',
  //   inputType: 'hours',
  //   defaultQuantity: 8,
  //   label: 'Timmar',
  //   requiresAbsenceDates: false,
  //   effect_type: 'dec',
  //   description: 'Karens Månadsavlönad',
  //   icon: AlertTriangle,
  //   color: '#ef4444'
  // },

  // ============================================================================
  // PARENTAL LEAVE (5 types) - ACTIVE & LEGALLY REQUIRED FOR AGI
  // ============================================================================
  '3107': { // VAB - Vård av barn (Temporary parental allowance)
    unit: 'Tim',
    inputType: 'hours',
    defaultQuantity: 1,
    label: 'Timmar',
    requiresAbsenceDates: true,
    absenceType: 'TILLFALLIG_FORALDRAPENNING',
    effect_type: 'dec',
    description: 'VAB - Vård av barn',
    isPopular: true,
    icon: Baby,
    color: '#ef4444'
  },

  // ============================================================================
  // VACATION - DISABLED (uncommon, can re-enable later)
  // ============================================================================
  // '3201': { // Obetald semester - DISABLED
  //   unit: 'Dgr',
  //   inputType: 'days',
  //   defaultQuantity: 1,
  //   label: 'Dagar',
  //   requiresAbsenceDates: false,
  //   effect_type: 'dec',
  //   description: 'Obetald semester',
  //   icon: Calendar,
  //   color: '#10b981'
  // },

  '3301': { // Föräldraledighet timavdrag
    unit: 'Tim',
    inputType: 'hours',
    defaultQuantity: 1,
    label: 'Timmar',
    requiresAbsenceDates: true,
    absenceType: 'FORALDRAPENNING',
    effect_type: 'dec',
    description: 'Föräldraledighet timavdrag',
    isPopular: true,
    icon: Baby,
    color: '#ef4444'
  },
  '3305': { // Föräldraledighet dagsavdrag
    unit: 'Dgr',
    inputType: 'days',
    defaultQuantity: 1,
    label: 'Dagar',
    requiresAbsenceDates: true,
    absenceType: 'FORALDRAPENNING',
    effect_type: 'dec',
    description: 'Föräldraledighet dagsavdrag',
    icon: Baby,
    color: '#ef4444'
  },
  '3318': { // Vård av barn 100% avdrag
    unit: 'Tim',
    inputType: 'hours',
    defaultQuantity: 1,
    label: 'Timmar',
    requiresAbsenceDates: true,
    absenceType: 'TILLFALLIG_FORALDRAPENNING',
    effect_type: 'dec',
    description: 'Vård av barn 100% avdrag',
    icon: Baby,
    color: '#ef4444'
  },
  '3321': { // 10-dagar vid barns födelse
    unit: 'Tim',
    inputType: 'hours',
    defaultQuantity: 1,
    label: 'Timmar',
    requiresAbsenceDates: true,
    absenceType: 'TILLFALLIG_FORALDRAPENNING',
    effect_type: 'dec',
    description: '10-dagar vid barns födelse',
    icon: Baby,
    color: '#ef4444'
  }
};

/**
 * Get wage type configuration by code
 */
export const getWageTypeConfig = (code) => {
  return COMPREHENSIVE_WAGE_TYPES[code] || null;
};

/**
 * Get all wage types that require absence dates
 */
export const getAbsenceWageTypes = () => {
  return Object.keys(COMPREHENSIVE_WAGE_TYPES)
    .filter(code => COMPREHENSIVE_WAGE_TYPES[code].requiresAbsenceDates);
};

/**
 * Get all wage types for AGI XML mapping
 */
export const getAgiMappedWageTypes = () => {
  return Object.keys(COMPREHENSIVE_WAGE_TYPES)
    .filter(code => COMPREHENSIVE_WAGE_TYPES[code].agiMapping);
};

/**
 * Get wage types by category (first digit)
 */
export const getWageTypesByCategory = (category) => {
  return Object.keys(COMPREHENSIVE_WAGE_TYPES)
    .filter(code => code.startsWith(category));
};

/**
 * Get popular wage types
 */
export const getPopularWageTypes = () => {
  return Object.keys(COMPREHENSIVE_WAGE_TYPES)
    .filter(code => COMPREHENSIVE_WAGE_TYPES[code].isPopular);
};

/**
 * Get wage types that require absence dates (for compatibility)
 */
export const ABSENCE_WAGE_TYPES = getAbsenceWageTypes();

export default COMPREHENSIVE_WAGE_TYPES;
